import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { UserProfile } from '@biasafe.models';
import { crossTabStorage } from '@/shared/utils/crossTabStorage';
import { resetAllStores } from '@/shared/utils/resetStores';

interface UserState {
  user: UserProfile | null;
  isAuthenticated: boolean;

  setUser: (user: UserProfile) => void;
  setAuthenticated: () => void;
  logout: () => void;
}

export const useUserStore = create<UserState>()(
  devtools(
    persist(
      (set) => ({
        user: null,
        isAuthenticated: false,

        setUser: (user) =>
          set({ user, isAuthenticated: true }, false, 'user/setUser'),

        setAuthenticated: () =>
          set({ isAuthenticated: true }, false, 'user/setAuthenticated'),

        logout: () => {
          // Clear the session flag FIRST, then clean up.
          //
          // The old order ran resetAllStores() before the set(), which made
          // logging out conditional on ~10 unrelated stores all resetting
          // without throwing. resetAllStores reaches into six other modules
          // and the query client; one TypeError anywhere in there (a store
          // still undefined under an import cycle, a renamed reset action)
          // and the set() below never ran — leaving `isAuthenticated: true`
          // persisted in localStorage. That flag is what the router's auth
          // gate reads, so the very next page load walked straight back into
          // the protected tree with a dead session cookie.
          //
          // Cleanup is best-effort and must never be able to keep a user
          // logged in.
          set({ user: null, isAuthenticated: false }, false, 'user/logout');
          try {
            resetAllStores();
          } catch (err) {
            console.error('[auth] store cleanup failed during logout', err);
          }
        },
      }),
      {
        name: 'user-storage',
        storage: crossTabStorage,
        // `orgRole` is deliberately stripped before persisting.
        //
        // It decides whether admin-only affordances are drawn, and a
        // persisted copy outlives the fact it describes: an admin demoted
        // in another session would keep `'admin'` in localStorage across
        // reloads until the next `/user` fetch, and localStorage is
        // user-editable besides. Rehydrating without it means the store
        // starts as "not an admin" and only becomes one once the server
        // says so this session — the same fail-closed default the API uses
        // when it can't resolve a local identity.
        //
        // The storage is JSON-backed, so `undefined` is dropped from the
        // serialized payload rather than written as a null.
        //
        // This costs a beat on reload: AuthLayout refetches `/user` and
        // calls setUser, so admin controls appear a moment after the rest
        // of the page. That's the intended trade — never show privileged
        // controls to someone the server won't let use them.
        partialize: (state) => ({
          user: state.user ? { ...state.user, orgRole: undefined } : null,
          isAuthenticated: state.isAuthenticated,
        }),
        // partialize only governs what gets WRITTEN. Rehydration reads
        // whatever is already in localStorage and merges it in, so without
        // this the fix would miss the exact population it's for: everyone
        // running the previous build has an orgRole sitting in storage
        // right now, and it would be read back on their next load. Strip it
        // on the way in too — a hand-edited value is rejected by the same
        // path.
        merge: (persisted, current) => {
          const incoming = (persisted ?? {}) as Partial<UserState>;
          return {
            ...current,
            ...incoming,
            user: incoming.user
              ? { ...incoming.user, orgRole: undefined }
              : current.user,
          };
        },
      },
    ),
  ),
);

/**
 * The caller's role in their organization — `'admin'` or `'member'`.
 *
 * This is `UserProfile.orgRole` (the local `users` row), NOT
 * `UserProfile.role` (the identity-provider claim). They are different
 * fields and only this one is what the API authorizes against:
 * `_require_admin` and `_can_manage_collaborators` both read `org_role`.
 *
 * Returns `undefined` until the profile query resolves this session — it is
 * not persisted, so every reload starts here. Callers must treat `undefined`
 * as "not an admin"; see `useIsOrgAdmin`.
 */
export const useOrgRole = (): string | undefined =>
  useUserStore((s) => s.user?.orgRole);

/**
 * Whether the caller is an org admin, for hiding admin-only affordances so
 * they aren't rendered and then answered with a 403.
 *
 * DISPLAY HINT ONLY — never a security boundary. It decides whether a
 * control is drawn, nothing more; every privileged action stays enforced
 * server-side, which re-derives `org_role` per request.
 *
 * Fails closed twice over: `orgRole` is stripped before persisting (see
 * `partialize`), so a reload starts as non-admin until `/user` says
 * otherwise, and an absent or unrecognised role is not admin.
 */
export const useIsOrgAdmin = (): boolean =>
  useUserStore((s) => s.user?.orgRole === 'admin');
